"use client";

import type { AccountStreaks } from "@decibeltrade/sdk";
import { useTranslations } from "next-intl";
import { css } from "styled-system/css";
import { flex, stack } from "styled-system/patterns";

import { useGlobalPointsStatsQuery } from "@/contexts/queries/global-points-stats";
import { usePredepositTransitioned } from "@/contexts/queries/points/predeposit-transitioned";
import { usePredepositUsdcRewards } from "@/contexts/queries/points/predeposit-usdc-rewards";
import { useOwnerPointsRankQuery } from "@/contexts/queries/points-leaderboard";
import { useAffiliateCodesQuery } from "@/contexts/queries/referral/use-get-affiliate-codes";
import { useReferrerStatsQuery } from "@/contexts/queries/referrer-stats";
import { useAccountStreaksQuery } from "@/contexts/queries/streaks";
import { useTierInfoQuery } from "@/contexts/queries/tier";
import { useAuthState } from "@/hooks/use-auth-state";
import { NumberFormatter } from "@/utils/number-formatter";

import {
  OverviewCard,
  PointsLeaderboard,
  ReferralLinksCard,
  StatsSection,
  StreaksCard,
  TierCard,
  TradeLoudBanner,
} from "./components";
import { BackgroundImage } from "./components/background-image";
import { GradientText } from "./components/gradient-text";
import { SeasonHeroHeader } from "./components/season-hero-header";

const CURRENT_SEASON = 1;

// Mock data - replace with API calls
const mockOverviewData = {
  totalPoints: "30,035",
  referrals: "1/5",
  lastUpdated: "2025/12/15, 14:59:32",
  seasonNumber: CURRENT_SEASON,
};

const TIER_DISPLAY = {
  gold: { name: "Gold", image: "/images/points/tier-gold-record.webp" },
  doublePlatinum: { name: "Platinum", image: "/images/points/tier-platinum-record.webp" },
  diamond: { name: "Diamond", image: "/images/points/tier-diamond-record.webp" },
} as const;

const PointsPage = () => {
  const t = useTranslations("general");
  const { accountAddress } = useAuthState();
  const { data: rankData, isLoading: isRankLoading } = useOwnerPointsRankQuery({
    ownerAddr: accountAddress,
  });
  const { data: tierData } = useTierInfoQuery({
    ownerAddr: accountAddress,
  });
  const { data: affiliateCodesData } = useAffiliateCodesQuery({
    ownerAccount: accountAddress,
  });
  const { data: referrerStatsData } = useReferrerStatsQuery(accountAddress);
  const { data: hasEarnedS0 } = usePredepositTransitioned(accountAddress);
  const { data: predepositRewardsData } = usePredepositUsdcRewards(accountAddress);
  const { data: globalStats } = useGlobalPointsStatsQuery();
  const { data: streaksData } = useAccountStreaksQuery({
    ownerAddr: accountAddress,
  });

  const allCodes = affiliateCodesData?.codes.filter((code) => code.source !== "predeposit") ?? [];
  const hasAffiliateCode = allCodes.some((code) => code.is_affiliate);
  const codes = hasAffiliateCode ? allCodes.filter((code) => code.is_affiliate) : allCodes;
  const volumeThresholdMet = affiliateCodesData?.volume_threshold_met ?? false;

  const referralCodes = codes.map((code) => ({
    code: code.referral_code,
    used: !code.is_active || code.usage_count >= code.max_usage,
  }));
  const totalReferrals = referrerStatsData?.total_referrals ?? 0;
  const formattedReferrals = totalReferrals > 0 ? totalReferrals.toLocaleString() : "0";

  const referralAmps = rankData?.items[0]?.referral_amps;
  const formattedReferralPoints =
    referralAmps != null ? NumberFormatter.compactHeading(referralAmps) : undefined;

  const totalAmps = rankData?.items[0]?.total_amps;
  const formattedPoints = isRankLoading
    ? t("loadingPlaceholder")
    : totalAmps != null
      ? NumberFormatter.compactHeading(totalAmps)
      : "0";

  const userRank = rankData?.items[0]?.rank;
  const formattedRank = isRankLoading
    ? t("loadingPlaceholder")
    : userRank != null
      ? `#${userRank.toLocaleString()}`
      : "\u2014";

  const goldTier = tierData?.tiers.find((tier) => tier.name === "gold");
  const doublePlatinumTier = tierData?.tiers.find((tier) => tier.name === "doublePlatinum");
  const diamondTier = tierData?.tiers.find((tier) => tier.name === "diamond");

  const tierDataWithRealPoints = {
    currentTier: (tierData?.current_tier ?? "gold") as "gold" | "doublePlatinum" | "diamond",
    totalPoints: formattedPoints,
    rank: formattedRank,
    tiers: {
      gold: {
        ...TIER_DISPLAY.gold,
        pointsRequired: NumberFormatter.compactHeading(goldTier?.hz_threshold ?? 0),
        progress: goldTier?.progress ?? 0,
      },
      doublePlatinum: {
        ...TIER_DISPLAY.doublePlatinum,
        pointsRequired: NumberFormatter.compactHeading(doublePlatinumTier?.hz_threshold ?? 0),
        progress: doublePlatinumTier?.progress ?? 0,
      },
      diamond: {
        ...TIER_DISPLAY.diamond,
        pointsRequired: NumberFormatter.compactHeading(diamondTier?.hz_threshold ?? 0),
        progress: diamondTier?.progress ?? 0,
      },
    },
  };

  const overviewDataWithRealPoints = {
    ...mockOverviewData,
    totalPoints: formattedPoints,
    rank: formattedRank,
    referrals: formattedReferrals,
    confirmed: hasEarnedS0 ?? false,
    ...(formattedReferralPoints != null && { referralPoints: formattedReferralPoints }),
    predepositUsdcReward: predepositRewardsData?.usdc_reward,
  };

  return (
    <div
      className={stack({
        textAlign: "center",
        alignItems: "center",
        justifyContent: "flex-start",
        position: "relative",
        zIndex: "[1]",
        minH: "[100vh]",
        gap: "0",
        pb: "0",
      })}
    >
      <HeroSection />
      <MobileCardsSection
        overviewData={overviewDataWithRealPoints}
        tierData={tierDataWithRealPoints}
        referralCodes={referralCodes}
        volumeThresholdMet={volumeThresholdMet}
        streaksData={streaksData}
      />
      <DesktopCardsSection
        overviewData={overviewDataWithRealPoints}
        tierData={tierDataWithRealPoints}
        referralCodes={referralCodes}
        volumeThresholdMet={volumeThresholdMet}
        streaksData={streaksData}
      />
      <StatsSection
        usersWithPoints={globalStats ? globalStats.total_users.toLocaleString() : "0"}
        pointsDistributed={
          globalStats ? Math.round(globalStats.total_amps_distributed).toLocaleString() : "0"
        }
      />
      <LeaderboardSection />
      <TradeLoudBanner />
      <BackgroundImage src="/images/pre-deposits/background.webp" />
    </div>
  );
};

export default PointsPage;

function HeroSection() {
  const t = useTranslations("points");

  return (
    <div
      className={stack({
        gap: { base: "12", md: "16", lg: "20" },
        textAlign: "center",
        pt: { base: "[80px]", md: "[84px]", lg: "[84px]" },
        pb: { base: "20", md: "24", lg: "28" },
        w: "full",
        alignItems: "center",
      })}
    >
      <SeasonHeroHeader
        season={t("hero.season", { seasonNumber: CURRENT_SEASON })}
        title={t("hero.title")}
      />

      <div
        className={css({
          display: "grid",
          gridTemplateColumns: "1fr",
          gap: "32",
          textAlign: "center",
          mt: { base: "12", md: "16", lg: "20" },
          mx: "auto",
          px: { base: "16", md: "56", lg: "136" },
          w: "full",
          justifyItems: "center",
          pb: "4",
        })}
      >
        <div className={stack({ alignItems: "center", gap: "0" })}>
          <GradientText
            className={css({
              textStyle: { base: "display.xs", md: "display.md", lg: "display.lg" },
              display: "block",
              textAlign: "center",
            })}
          >
            {t("hero.headlinePart1")} {t("hero.headlinePart2")}
          </GradientText>
          <GradientText
            className={css({
              textStyle: { base: "display.xs", md: "display.md", lg: "display.lg" },
              display: "block",
              textAlign: "center",
            })}
          >
            {t("hero.headlinePart3")} {t("hero.headlinePart4")}
          </GradientText>
        </div>
        <span
          className={css({
            textStyle: "subheading.sm",
            color: "text.secondary",
            maxWidth: "[644px]",
            mx: "auto",
          })}
        >
          {t("hero.description")}
        </span>
      </div>
    </div>
  );
}

type TierLevel = "gold" | "doublePlatinum" | "diamond";

interface TierDataProps {
  currentTier: TierLevel;
  totalPoints: string;
  rank: string;
  tiers: Record<
    TierLevel,
    { name: string; image: string; pointsRequired: string; progress: number }
  >;
}

interface CardsSectionProps {
  overviewData: typeof mockOverviewData & {
    rank: string;
    referralPoints?: string;
    predepositUsdcReward?: number;
  };
  tierData: TierDataProps;
  referralCodes: Array<{ code: string; used: boolean }>;
  volumeThresholdMet: boolean;
  streaksData: AccountStreaks | undefined;
}

function MobileCardsSection({
  overviewData,
  tierData,
  referralCodes,
  volumeThresholdMet,
  streaksData,
}: CardsSectionProps) {
  return (
    <div
      className={stack({
        gap: "16",
        alignItems: "center",
        w: "full",
        maxW: "[1244px]",
        mx: "auto",
        px: "16",
        pt: "24",
        pb: "40",
        display: { base: "flex", lg: "none" },
      })}
    >
      <OverviewCard {...overviewData} />
      <StreaksCard streaksData={streaksData} />
      <ReferralLinksCard referralCodes={referralCodes} volumeThresholdMet={volumeThresholdMet} />
      <TierCard {...tierData} />
    </div>
  );
}

function DesktopCardsSection({
  overviewData,
  tierData,
  referralCodes,
  volumeThresholdMet,
  streaksData,
}: CardsSectionProps) {
  return (
    <div
      className={stack({
        gap: "24",
        alignItems: "stretch",
        w: "full",
        maxW: "[1244px]",
        mx: "auto",
        px: "24",
        pt: "32",
        pb: "48",
        display: { base: "none", lg: "flex" },
      })}
    >
      {/* Overview card - full width at top */}
      <OverviewCard {...overviewData} />

      {/* Tier and Streaks/Referral cards */}
      <div
        className={flex({
          gap: "24",
          alignItems: "stretch",
          w: "full",
        })}
      >
        {/* Tier card - left column (60%) */}
        <div
          className={stack({
            gap: "24",
            alignItems: "stretch",
            flex: "[6]",
            minW: "0",
          })}
        >
          <TierCard {...tierData} />
        </div>

        {/* Streaks and Referral cards - right column (40%) */}
        <div
          className={stack({
            gap: "24",
            alignItems: "stretch",
            flex: "[4]",
            minW: "0",
          })}
        >
          <StreaksCard streaksData={streaksData} />
          <ReferralLinksCard
            referralCodes={referralCodes}
            volumeThresholdMet={volumeThresholdMet}
          />
        </div>
      </div>
    </div>
  );
}

function LeaderboardSection() {
  return (
    <div
      className={css({
        w: "full",
        maxW: "[1244px]",
        mx: "auto",
        px: { base: "16", md: "24" },
        pt: { base: "[40px]", md: "[64px]" },
        pb: { base: "[60px]", md: "[80px]" },
      })}
    >
      <PointsLeaderboard />
    </div>
  );
}
